import { useSearchParams } from 'react-router-dom';

import { useI18n } from '../i18n/index.js';
import styles from '../routes/pageStyles.module.css';

const TABS = ['timeline', 'picks'] as const;

export type ProfileTab = (typeof TABS)[number];

/**
 * 어느 탭인지는 **주소에 남긴다**(`?tab=picks`).
 *
 * 새로고침하거나 뒤로 가기를 눌렀을 때 보던 탭이 아니라 첫 탭으로 돌아가면
 * 방금 하던 일을 잃는다. 남의 프로필에서는 링크를 그대로 나눠 줄 수도 있다.
 *
 * 모르는 탭 이름은 첫 탭으로 떨어진다 — 남의 프로필 주소에 `?tab=analytics`를
 * 붙여도 없는 화면이 열리지 않는다.
 */
export function useProfileTab(): [ProfileTab, (next: ProfileTab) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab') as ProfileTab | null;
  const tab: ProfileTab = raw !== null && TABS.includes(raw) ? raw : 'timeline';

  const set = (next: ProfileTab) => {
    const updated = new URLSearchParams(params);
    /*
     * 첫 탭은 주소에서 뺀다. `?tab=timeline`과 그냥 주소가 같은 화면인데
     * 주소가 둘이면 공유된 링크가 갈린다.
     */
    if (next === 'timeline') {
      updated.delete('tab');
    } else {
      updated.set('tab', next);
    }
    setParams(updated, { replace: true });
  };

  return [tab, set];
}

/**
 * 프로필의 두 갈래 — 인생(타임라인)과 취향(픽).
 *
 * **내 프로필과 남의 프로필이 같은 모양이어야 한다.** 남의 것을 보다가 내
 * 것으로 왔을 때 구조가 달라지면 같은 서비스로 안 읽힌다. 그래서 분석은
 * 탭이 아니라 **남의 프로필에서 인연 버튼이 있는 자리**에 둔다 — 탭이 하나
 * 더 있으면 두 화면의 뼈대가 달라진다.
 */
export function ProfileTabs({
  tab,
  onTab,
}: {
  tab: ProfileTab;
  onTab: (next: ProfileTab) => void;
}) {
  const { t } = useI18n();

  return (
    <div className={styles.tabs} role="tablist">
      {TABS.map((name) => (
        <button
          key={name}
          type="button"
          role="tab"
          className={styles.tab}
          aria-selected={tab === name}
          onClick={() => onTab(name)}
        >
          {t(`home.${name}`)}
        </button>
      ))}
    </div>
  );
}
